import type { ToolCallMessagePartProps } from '@assistant-ui/react';
import { useAuiState } from '@assistant-ui/react';
import { useIntersection } from '@mantine/hooks';
import cx from 'clsx';
import { motion } from 'motion/react';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { MantineColor } from '#core/lib/formatters/colors.ts';

import { ToolChromeContext } from './ToolChromeContext.ts';
import { ToolContentArea } from './ToolContentArea.tsx';
import { ToolContentErrorFallback } from './ToolContentBubble.tsx';
import { ToolDetails } from './ToolDetails.tsx';
import classes from './ToolDisplay.module.css';
import { ToolStatusRow } from './ToolStatusRow.tsx';
import { usePartStatus, useToolStatusKind } from '#plugin-chat/hooks/usePartStatus.ts';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';
import type {
  AnyToolLabelValues,
  AnyToolStatusRenderers,
  StyledIcon,
} from '#plugin-chat/lib/tool/message-formatters.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

/**
 * assistant-ui mounts tool components with the part's props spread in, so
 * the chrome's own props must stay structurally compatible with a
 * tool-call part component — everything is optional and the part-derived
 * fields (`resume`, `interrupt`) share the part-prop names/types. This
 * lets `ToolDisplay` double as the `tools.Fallback` component for
 * unregistered tools: the zero-config mount renders the default
 * conventional status rows.
 */
export interface ToolDisplayProps {
  /** Per-status render overrides (`<Tool render>`). */
  renderers?: AnyToolStatusRenderers;
  /** Tool color: derives the default complete-row color (`<Tool color>`). */
  color?: MantineColor;
  /** Default complete-row icon (`<Tool icon>`). */
  icon?: StyledIcon;
  /** Pin the tool chrome to the viewport top while its content scrolls (`<Tool sticky>`). */
  sticky?: boolean;
  /** i18n interpolation values for the conventional status/progress labels (`<Tool labelValues>`). */
  labelValues?: AnyToolLabelValues;
  resume?: ToolCallMessagePartProps<Record<string, unknown>, unknown>['resume'];
  interrupt?: ToolCallMessagePartProps<Record<string, unknown>, unknown>['interrupt'];
}

export function ToolDisplay({ renderers, color, icon, sticky, labelValues, resume, interrupt }: ToolDisplayProps) {
  const status = usePartStatus();
  const { ref, entry } = useIntersection({
    threshold: 1,
    rootMargin: '-25px 0px 0px 0px',
  });
  const toolName = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.toolName : undefined));
  const isStuck = entry?.isIntersecting === false && entry.intersectionRect.top === entry.rootBounds?.top;
  const [asideElement, setAsideElement] = useState<HTMLElement | null>(null);

  return (
    <ToolChromeContext value={{ toolName, color, icon, labelValues, resume, interrupt, asideElement }}>
      <div
        className={cx(
          classes.toolDisplay,
          status.isCancelled && classes.cancelled,
          (status.isComplete || status.isCancelled) && classes.complete,
          sticky && classes.sticky,
          isStuck && classes.isStuck,
        )}
        data-tool-name={toolName}
        // The observer only feeds the sticky "stuck" styling — leave it
        // disconnected for non-sticky tools so scrolling never re-renders them.
        ref={sticky ? ref : undefined}
      >
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className={classes.status}
          initial={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <ErrorBoundary
            FallbackComponent={(props) => (
              <ToolContentArea>
                <div className={classes.contentBox}>
                  <ToolContentErrorFallback {...props} />
                </div>
              </ToolContentArea>
            )}
          >
            <ToolStatusBody interrupt={interrupt} renderers={renderers} resume={resume} />
          </ErrorBoundary>
          <ToolDetails />
        </motion.div>
      </div>
      <div className={classes.toolAside} ref={setAsideElement} />
    </ToolChromeContext>
  );
}

/**
 * Resolve and render the single active status renderer.
 *
 * Kept out of {@link ToolDisplay} itself so the per-token `args`/`result`
 * subscription lives only inside the region that renders from it, never
 * on the chrome root. Exactly one branch fires per render: the tool's
 * override for the active status kind, or the default conventional
 * {@link ToolStatusRow} when the kind has no override.
 */
function ToolStatusBody({
  renderers,
  resume,
  interrupt,
}: Pick<ToolDisplayProps, 'renderers' | 'resume' | 'interrupt'>) {
  const kind = useToolStatusKind();
  const { args = {}, result } = useToolPart<Record<string, unknown>, unknown>();

  if (renderers?.[kind] === undefined) {
    return <ToolStatusRow />;
  }
  if (kind === 'running') {
    return renderers.running?.({ args });
  }
  if (kind === 'awaitingInput') {
    // `resume` is part-supplied and always present on interrupt-capable
    // parts — the only parts whose status can derive to awaitingInput.
    return renderers.awaitingInput?.({ args, resume: resume as NonNullable<typeof resume>, interrupt });
  }
  if (kind === 'complete') {
    return renderers.complete?.({ args, result });
  }
  if (kind === 'error') {
    const envelope = parseResultEnvelope(result);
    return renderers.error?.({ args, result, error: envelope.kind === 'failure' ? envelope.errorMessage : '' });
  }
  if (kind === 'cancelled') {
    return renderers.cancelled?.({ args });
  }
  return kind satisfies never;
}
